import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { TableModel } from '../../../../components/table';
import { reuseRows } from '../../../../components/rows';
import { CompanySelectorPresenter, MISSING_COMPANY } from '../../../../components/company-selector/company-selector.presenter';
import { formatDate, formatTime, hasAnyRole } from '../../../../domain';
import { CompanyUsersApi } from './company-users.api';
import { CompanyApi } from '../companies/companies.api';
import { blockedAttemptScopes } from '../companies/blocked-attempt.scope';
import { CompanyUsersKeys } from './company-users.key';
import { CompanyUserDetailKeys } from './detail/company-user-detail.key';
import { CompanyUserFormKeys } from './form/company-user-form.key';
import { CompanyLinkScope, CompanyTabScope, CompanyUserScope, CompanyUsersColumnScope, CompanyUsersScope } from './company-users.scope';
import { ACTION_COLUMN, COMPANY_USER_TABS, LINK_COLUMNS, USER_COLUMNS } from './company-users.types';
/** A janela do indicador de acesso - ver `showAccessSummary`. */
const ACESSO_RECENTE_DIAS = 30;
const LOG = Logger.get('Mormaii.CompanyUsersPresenter');
// @Inject
const api = CompanyUsersApi.INSTANCE;
const companyApi = CompanyApi.INSTANCE;
/** O rotulo de cada aba. */
const TAB_LABELS = {
    usuarios: 'Usuários',
    solicitacoes: 'Solicitações'
};
/**
 * A empresa administrando quem faz parte dela.
 *
 * Duas abas, e elas sao a mesma pergunta em dois momentos: **Solicitacoes** decide
 * quem entra, e **Usuarios** administra quem ja esta - os administradores e os
 * profissionais que a empresa cadastrou.
 *
 * As duas listas sao buscadas JUNTAS, mesmo que so uma esteja a vista. A aba de
 * solicitacoes mostra um contador, e um contador que so aparece depois de alguem
 * clicar nao serve para nada: a pessoa precisa saber que ha o que decidir ANTES de ir
 * ate la.
 *
 * Esta area E a aterrissagem do conceito - ela nunca sai da tela -, entao o que vem
 * depois (cadastrar, corrigir um CPF, atribuir colaboradores) e desenhado por cima e
 * e place FILHO. Mesma regra que faz `companies/edit` ser filho da lista de empresas.
 */
export class CompanyUsersPresenter extends CubePresenter {
    constructor(app) {
        super(app, new CompanyUsersScope());
        /** Os modelos das duas tabelas, so para a geometria: a lista vem na ordem do servidor. */
        this.userTable = geometryOnly('company-users', USER_COLUMNS, () => this.app.currentSession?.id);
        this.linkTable = geometryOnly('company-links', LINK_COLUMNS, () => this.app.currentSession?.id);
        this.parentSlot = NOOP_VOID;
        this.dialogSlot = NOOP_VOID;
        this.at = { tab: 'usuarios' };
        /** O seletor de empresa - so o administrador GERAL o tem. */
        this.selector = new CompanySelectorPresenter(this.update, async (companyId) => {
            this.owner?.rememberFilterCompany(companyId);
            await this.go({ companyId });
        }, () => this.app.alert('error', MISSING_COMPANY.title, MISSING_COMPANY.text));
        /** As linhas cruas, para os dialogos filhos nao buscarem de novo. */
        this.users = [];
        this.pending = 0;
    }
    async applyParameters(intent, initialization, last) {
        const keys = new CompanyUsersKeys(this.app, intent);
        if (!(await keys.owner?.allowEntry('company-users', intent))) {
            return false;
        }
        keys.area = this;
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.owner = keys.owner;
        if (initialization) {
            this.scope.onNewAdmin = this.action(this.onNew.bind(this, 'admin'));
            this.scope.onNewProfessional = this.action(this.onNew.bind(this, 'profissional'));
            // So o admin GERAL escolhe empresa: o de empresa administra a dele, e
            // oferecer um seletor a ele sugeriria um poder que o servidor recusa.
            // O seletor proprio so existe quando a escolha NAO esta no cabecalho - dois
            // controles para a mesma coisa acabam discordando.
            this.scope.companyChooser =
                (keys.owner?.canChooseCompany ?? false) && !(keys.owner?.hasHeaderChooser ?? false);
            if (this.scope.companyChooser) {
                // Sem rotulo no campo: o painel que o cerca ja se chama "Empresa", e
                // a palavra duas vezes uma embaixo da outra le como um defeito.
                this.selector.describe('', 'Escolha uma empresa');
                /*
                 * Quem administra empresas escolhe entre as DELE; o administrador
                 * geral escolhe entre todas, e ai o seletor busca no servidor.
                 */
                const minhas = keys.owner?.myCompanies ?? [];
                if (minhas.length > 0) {
                    this.selector.restrictTo(minhas);
                }
                this.scope.companySelector = this.selector.scope;
            }
            this.scope.tabs = COMPANY_USER_TABS.map((tab) => this.tabOf(tab));
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        this.dialogSlot = keys.dialogSlot;
        // O seletor segue o ENDERECO, e a cada passagem - nao so na montagem. Ele
        // sobrevive a navegacao, entao voltar para ca sem parametro o deixava
        // mostrando a ultima empresa escolhida sobre uma lista que ja era de outra.
        if (this.scope.companyChooser && keys.companyId !== this.at.companyId) {
            await this.selector.showSelected(keys.companyId);
        }
        if (last) {
            // Nada mais profundo esta aberto, entao o que a caixa de dialogo
            // guardava se foi.
            this.dialogSlot(undefined);
        }
        else {
            // Um dialogo esta aberto: a area CONTINUA na tela atras dele, e e por
            // isso que o dialogo vai para o slot de dialogo do shell.
            keys.dialogSlot = this.dialogSlot;
        }
        this.owner?.showSection('company-users', 'Acesso ao painel', 'Administradores, profissionais e as solicitações de vínculo');
        this.parentSlot(this.scope);
        this.scope.userLayout = this.userTable.show(this);
        this.scope.linkLayout = this.linkTable.show(this);
        this.scope.userColumns = this.buildColumns(this.userTable, USER_COLUMNS, (columns) => {
            this.scope.userColumns = columns;
        }, () => this.scope.userColumns);
        this.scope.linkColumns = this.buildColumns(this.linkTable, LINK_COLUMNS, (columns) => {
            this.scope.linkColumns = columns;
        }, () => this.scope.linkColumns);
        /*
         * Sem empresa no endereco, cai na PADRAO de quem esta logado.
         *
         * Para o administrador geral ela e indefinida - e ele ve o convite a escolher.
         * Para quem administra uma ou mais, e a primeira delas: o servidor recusa agir
         * sem saber qual, e escolher em silencio seria pior - por isso a escolha fica
         * a vista no seletor.
         */
        const empresa = keys.owner?.companyOnArrival(keys.companyId);
        if (empresa !== undefined && this.scope.companyChooser) {
            await this.selector.showSelected(empresa);
        }
        const target = { tab: keys.tab, companyId: empresa };
        /*
         * A MESMA empresa da lista que ja esta na tela: nada a buscar.
         *
         * Abrir e fechar um dialogo filho (o cadastro, a ficha) passa por aqui, porque
         * esta area esta no caminho dele. Recarregar a cada passagem punha a tabela em
         * "carregando" um instante antes de o dialogo abrir - o piscar relatado em
         * 08/10/2026. Quem MUDA o dado avisa por `refresh()`, e e entao que se busca.
         */
        if (this.loadedFor !== undefined && this.loadedFor === String(empresa ?? '')) {
            this.at = target;
            this.showRequest(target);
            return true;
        }
        await this.moveTo(target);
        return true;
    }
    publishParameters(intent) {
        const keys = new CompanyUsersKeys(this.app, intent);
        keys.tab = this.at.tab;
        keys.companyId = this.companyIdToPublish;
    }
    /** Ver `RestrictedPresenter.companyIdInAddress`: com cabecalho, quem publica e ele. */
    get companyIdToPublish() {
        return this.owner?.companyIdInAddress(this.at.companyId);
    }
    // ========== O QUE OS DIALOGOS PEDEM ==========
    /** Um usuario ja em maos, para o dialogo de CPF nao busca-lo de novo. */
    userOf(userId) {
        return this.users.find((user) => String(user.userId) === String(userId));
    }
    /** O nome de um profissional, para o dialogo de atribuicao dizer de quem fala. */
    professionalName(professionalId) {
        return this.users.find((user) => String(user.professionalId) === String(professionalId))?.name;
    }
    /** Quem mudou o dado avisa quem o mostra. */
    async refresh() {
        await this.moveTo(this.at);
    }
    // ========== NAVEGACAO ==========
    async go(change) {
        await this.moveTo({ ...this.at, ...change });
        this.updateHistory();
    }
    async moveTo(target) {
        this.at = target;
        this.showRequest(target);
        // O admin geral sem empresa escolhida nao tem o que buscar - e pedir sem
        // escopo devolveria erro do servidor em vez de um convite a escolher.
        this.scope.needsCompany = this.scope.companyChooser && !target.companyId;
        this.loadedFor = undefined;
        if (this.scope.needsCompany) {
            this.scope.loading = false;
            this.users = [];
            this.scope.users = [];
            this.scope.links = [];
            this.scope.blockedAttempts = [];
            return;
        }
        const token = ++this.pending;
        this.scope.loading = true;
        this.scope.error = undefined;
        try {
            const [users, links] = await Promise.all([
                api.fetchUsers(target.companyId),
                api.fetchPending(target.companyId)
            ]);
            if (token !== this.pending) {
                return;
            }
            this.users = users;
            this.loadedFor = String(target.companyId ?? '');
            // As linhas que ja estao na tela sao atualizadas, e nao recriadas - ver `reuseRows`.
            this.scope.users = reuseRows(this.scope.users, users, (user) => user.userId, (user, reuse) => this.userScopeOf(user, reuse));
            this.showMaster(users);
            this.showAccessSummary(users);
            this.scope.blockedAttempts = [];
            if (this.scope.podeCadastrarAdministrador && target.companyId) {
                // Em paralelo e sem segurar a lista: e um aviso, nao o conteudo da tela.
                void this.loadBlockedAttempts(target.companyId, token);
            }
            this.scope.links = reuseRows(this.scope.links, links, (link) => link.id, (link, reuse) => {
                const scope = reuse ?? new CompanyLinkScope();
                scope.identity = link.id;
                scope.name = link.patientName;
                scope.cpf = link.cpf;
                scope.email = link.email;
                scope.requestedAt = `${formatDate(link.requestedAt)} - ${formatTime(link.requestedAt)}`;
                scope.onApprove = this.action(this.onDecide.bind(this, link.id, link.patientName, true));
                scope.onReject = this.action(this.onDecide.bind(this, link.id, link.patientName, false));
                scope.update = this.update;
                return scope;
            });
            this.showRequest(target);
        }
        catch (caught) {
            if (token !== this.pending) {
                return;
            }
            LOG.warn('Could not load the company users', caught);
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível carregar os usuários.';
        }
        finally {
            if (token === this.pending) {
                this.scope.loading = false;
            }
        }
    }
    /**
     * Quem tentou entrar no painel enquanto a empresa estava bloqueada.
     *
     * O servidor decide quem pode ver (o master, depois da liberacao; a plataforma,
     * sempre): recusado, a tela segue sem o aviso, que nao e motivo de erro.
     */
    async loadBlockedAttempts(companyId, token) {
        try {
            const attempts = await companyApi.fetchBlockedSignInAttempts(companyId);
            if (token === this.pending) {
                this.scope.blockedAttempts = blockedAttemptScopes(attempts, this.update);
            }
        }
        catch (caught) {
            LOG.debug('Blocked sign-in attempts not available', caught);
        }
    }
    /**
     * Quantos da equipe entraram nos ultimos 30 dias - contado das LINHAS que a tela mostra.
     *
     * De proposito, e nao de um endpoint proprio: um numero calculado a parte pode discordar
     * da lista embaixo dele, e foi assim que este indicador viveu ate 23/09/2026 em Adocao e
     * Engajamento - contando uma populacao que aquela tela nao listava.
     *
     * TRINTA dias, fixo: aqui nao ha filtro de periodo, e a janela precisa estar dita no
     * rotulo para o numero significar alguma coisa. Era o padrao de onde ele veio.
     */
    showAccessSummary(users) {
        const limite = new Date();
        limite.setDate(limite.getDate() - ACESSO_RECENTE_DIAS);
        this.scope.acessosTotal = users.length;
        this.scope.acessosRecentes = users.filter((user) => {
            if (!user.lastAccessAt) {
                return false;
            }
            return new Date(user.lastAccessAt) >= limite;
        }).length;
    }
    /**
     * Quem mexe em administrador nesta empresa (B-30).
     *
     * A plataforma sempre; na empresa, so o master. Para saber se quem esta logado e o
     * master basta a lista que acabou de chegar - ele e uma das linhas. O servidor recusa
     * do mesmo jeito: isto so evita oferecer o que ele recusaria.
     */
    showMaster(users) {
        const session = this.app.currentSession;
        const plataforma = hasAnyRole(session?.roles ?? [], ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN']);
        const eu = users.find((user) => String(user.userId) === String(session?.id));
        const master = users.find((user) => user.master);
        this.scope.podeCadastrarAdministrador = plataforma || eu?.master === true;
        this.scope.avisoMaster = this.scope.podeCadastrarAdministrador
            ? ''
            : master
                ? `${master.name} é o administrador master desta empresa: só ele cadastra, desativa ou desliga administradores.`
                : 'Esta empresa está sem administrador master. Só o administrador da plataforma pode indicar um.';
    }
    userScopeOf(user, reuse) {
        const scope = reuse ?? new CompanyUserScope();
        scope.identity = user.userId;
        scope.name = user.name;
        scope.email = user.email;
        scope.cpf = user.cpf;
        scope.cpfFicticio = user.cpfFicticio;
        scope.papel = [user.administrador && 'Administrador', user.profissional && 'Profissional']
            .filter(Boolean)
            .join(' · ');
        scope.administrador = user.administrador;
        scope.master = user.master;
        scope.podeAlterarAcesso = user.podeAlterarAcesso;
        scope.profissional = user.profissional;
        scope.profissao = user.profissao ?? undefined;
        scope.atribuidos = user.colaboradoresAtribuidos;
        scope.ultimoAcesso = user.lastAccessAt ? formatDate(user.lastAccessAt) : '';
        scope.onOpen = this.action(this.onOpen.bind(this, user.userId));
        scope.onRemove = this.action(this.onRemoveUser.bind(this, user.userId, user.name));
        scope.update = this.update;
        return scope;
    }
    showRequest(target) {
        this.scope.tab = target.tab;
        for (const tab of this.scope.tabs) {
            tab.active = tab.key === target.tab;
            // O contador so existe onde ele muda uma decisao: saber que ha tres
            // pessoas esperando e o que faz alguem ir ate a aba.
            tab.badge = tab.key === 'solicitacoes' ? this.scope.links.length : 0;
        }
    }
    tabOf(tab) {
        const scope = new CompanyTabScope();
        scope.identity = tab;
        scope.key = tab;
        scope.label = TAB_LABELS[tab];
        scope.onSelect = this.action(async () => {
            await this.go({ tab });
        });
        scope.update = this.update;
        return scope;
    }
    // ========== ACOES ==========
    async onNew(role) {
        const keys = CompanyUserFormKeys.of(this.app);
        keys.role = role;
        await keys.flip();
    }
    /**
     * Abre a FICHA da pessoa.
     *
     * Uma porta so para tudo o que se configura nela. Antes eram duas - corrigir CPF e
     * atribuir colaboradores -, e cada uma mostrava um pedaco: quem abrisse a primeira
     * concluia, com razao, que so o CPF era editavel.
     */
    async onOpen(userId) {
        const keys = CompanyUserDetailKeys.of(this.app);
        keys.target = userId;
        await keys.flip();
    }
    /**
     * Aprova ou rejeita um pedido, e recarrega.
     *
     * Recarrega em vez de tirar a linha da lista na mao: aprovar muda as DUAS abas -
     * sai das solicitacoes e a pessoa passa a contar como colaborador -, e manter
     * isso em sincronia no cliente seria reimplementar no navegador o que o servidor
     * ja sabe.
     */
    async onDecide(requestId, nome, aprovar) {
        try {
            if (aprovar) {
                await api.approve(requestId, this.at.companyId);
                this.app.notify('Vínculo aprovado', `${nome} agora faz parte da empresa.`, 'Continuar', 'user-check');
            }
            else {
                await api.reject(requestId, this.at.companyId);
                this.app.notify('Solicitação recusada', `O pedido de ${nome} foi recusado.`, 'Continuar');
            }
            await this.moveTo(this.at);
        }
        catch (caught) {
            LOG.warn('Could not decide the request', caught);
            this.app.alert('error', 'Solicitação', messageOf(caught, 'Não foi possível concluir a decisão.'));
        }
    }
    /**
     * Desliga alguem da empresa - e PERGUNTA antes.
     *
     * Desligar um profissional corta junto o acesso dele aos colaboradores desta
     * empresa, e isso nao se desfaz com um clique: religar seria refazer as
     * atribuicoes uma a uma. E por isso que a pergunta existe.
     */
    async onRemoveUser(userId, nome) {
        this.app.confirm('Desligar da empresa', `${nome} perde o acesso ao painel desta empresa. Continuar?`, 'Desligar', async () => {
            await this.action(this.removeUser.bind(this, userId))();
        }, { cancelText: 'Cancelar', icon: 'account-remove' });
    }
    async removeUser(userId) {
        try {
            await api.removeUser(userId, this.at.companyId);
            await this.moveTo(this.at);
        }
        catch (caught) {
            LOG.warn('Could not remove the user', caught);
            this.app.alert('error', 'Desligar', messageOf(caught, 'Não foi possível desligar este usuário.'));
        }
    }
    /**
     * As colunas de uma das tabelas, na ordem que a pessoa arrastou. O nome identifica a
     * linha e a de acoes so guarda botoes: as duas nao saem do lugar. A ordem e
     * preferencia de quem le, e nao vai para o endereco.
     */
    buildColumns(table, columns, publish, 
    /** As colunas que estao na tela AGORA - lidas na hora, e nao guardadas. */
    current) {
        const all = columns.map((column) => column.field);
        const order = table.arrange(all, FIXED_COLUMNS);
        table.setVisible(order);
        // A coluna que ja esta na tela e o mesmo scope, atualizado - ver `reuseRows`.
        return reuseRows(current(), order, (field) => field, (field, reused) => {
            const scope = reused ?? new CompanyUsersColumnScope();
            scope.identity = field;
            scope.field = field;
            scope.label = columns.find((column) => column.field === field).label;
            scope.movable = table.isMovable(all, field, FIXED_COLUMNS);
            scope.onDrop = (source) => {
                if (table.move(all, source, field, FIXED_COLUMNS)) {
                    publish(this.buildColumns(table, columns, publish, current));
                }
            };
            scope.update = this.update;
            return scope;
        });
    }
}
/** A mensagem do servidor, que costuma explicar melhor que um texto generico. */
function messageOf(caught, fallback) {
    return caught instanceof Error && caught.message ? caught.message : fallback;
}
/** O nome - a primeira - e a de acoes - a ultima - ficam; as outras se arrastam. */
const FIXED_COLUMNS = { start: 1, end: 1 };
function geometryOnly(name, columns, account) {
    const model = new TableModel({ name, account });
    model.setColumns(columns.map((column) => ({ field: column.field, resizable: column.field !== ACTION_COLUMN })));
    model.setVisible(columns.map((column) => column.field));
    return model;
}
//# sourceMappingURL=company-users.presenter.js.map